<template>
  <div
    :is="as"
    class="Box"
    :class="[
      visuallyHidden && 'visuallyHidden',
      printHidden && 'printHidden',
      as === 'ul' && 'listReset',
    ]"
    :style="styles"
  >
    <slot></slot>
  </div>
</template>

<script>
import { getResponsiveProps } from '@/components/polaris/polarisUtils';

export default {
  name: 'PBox',
  props: {
    as: { type: String, default: 'div' },
    printHidden: { type: Boolean },
    visuallyHidden: { type: Boolean },

    background: { type: String },
    borderColor: { type: String },
    borderStyle: { type: String },
    borderWidth: { type: String },
    borderBlockStartWidth: { type: String },
    borderBlockEndWidth: { type: String },
    borderInlineStartWidth: { type: String },
    borderInlineEndWidth: { type: String },
    borderRadius: { type: String },
    borderEndStartRadius: { type: String },
    borderEndEndRadius: { type: String },
    borderStartStartRadius: { type: String },
    borderStartEndRadius: { type: String },
    color: { type: String },
    minHeight: { type: String },
    minWidth: { type: String },
    maxWidth: { type: String },
    overflowX: { type: String },
    overflowY: { type: String },
    outlineColor: { type: String },
    outlineStyle: { type: String },
    outlineWidth: { type: String },
    padding: { type: [String, Object] },
    paddingBlock: { type: String },
    paddingBlockStart: { type: String },
    paddingBlockEnd: { type: String },
    paddingInline: { type: String },
    paddingInlineStart: { type: String },
    paddingInlineEnd: { type: String },
    shadow: { type: String },
    width: { type: String },
    position: { type: String },
    insetBlockStart: { type: String },
    insetBlockEnd: { type: String },
    insetInlineStart: { type: String },
    insetInlineEnd: { type: String },
    zIndex: { type: String },
    opacity: { type: String },
  },
  computed: {
    styles() {
      const borderStyleValue = this.borderStyle
        ? this.borderStyle
        : this.borderColor ||
          this.borderWidth ||
          this.borderBlockStartWidth ||
          this.borderBlockEndWidth ||
          this.borderInlineStartWidth ||
          this.borderInlineEndWidth
        ? 'solid'
        : undefined;

      // eslint-disable-next-line no-nested-ternary
      const outlineStyleValue = this.outlineStyle
        ? this.outlineStyle
        : this.outlineColor || this.outlineWidth
        ? 'solid'
        : undefined;

      return {
        '--pc-box-color': this.color ? `var(--p-color-${this.color})` : undefined,
        '--pc-box-background': this.background ? `var(--p-color-${this.background})` : undefined,
        // eslint-disable-next-line no-nested-ternary
        '--pc-box-border-color': this.borderColor
          ? this.borderColor === 'transparent'
            ? 'transparent'
            : `var(--p-color-${this.borderColor})`
          : undefined,
        '--pc-box-border-style': borderStyleValue,
        '--pc-box-border-radius': this.borderRadius
          ? `var(--p-border-radius-${this.borderRadius})`
          : undefined,
        '--pc-box-border-end-start-radius': this.borderEndStartRadius
          ? `var(--p-border-radius-${this.borderEndStartRadius})`
          : undefined,
        '--pc-box-border-end-end-radius': this.borderEndEndRadius
          ? `var(--p-border-radius-${this.borderEndEndRadius})`
          : undefined,
        '--pc-box-border-start-start-radius': this.borderStartStartRadius
          ? `var(--p-border-radius-${this.borderStartStartRadius})`
          : undefined,
        '--pc-box-border-start-end-radius': this.borderStartEndRadius
          ? `var(--p-border-radius-${this.borderStartEndRadius})`
          : undefined,
        '--pc-box-border-width': this.borderWidth
          ? `var(--p-border-width-${this.borderWidth})`
          : undefined,
        '--pc-box-border-block-start-width': this.borderBlockStartWidth
          ? `var(--p-border-width-${this.borderBlockStartWidth})`
          : undefined,
        '--pc-box-border-block-end-width': this.borderBlockEndWidth
          ? `var(--p-border-width-${this.borderBlockEndWidth})`
          : undefined,
        '--pc-box-border-inline-start-width': this.borderInlineStartWidth
          ? `var(--p-border-width-${this.borderInlineStartWidth})`
          : undefined,
        '--pc-box-border-inline-end-width': this.borderInlineEndWidth
          ? `var(--p-border-width-${this.borderInlineEndWidth})`
          : undefined,
        '--pc-box-min-height': this.minHeight,
        '--pc-box-min-width': this.minWidth,
        '--pc-box-max-width': this.maxWidth,
        '--pc-box-outline-color': this.outlineColor
          ? `var(--p-color-${this.outlineColor})`
          : undefined,
        '--pc-box-outline-style': outlineStyleValue,
        '--pc-box-outline-width': this.outlineWidth
          ? `var(--p-border-width-${this.outlineWidth})`
          : undefined,
        '--pc-box-overflow-x': this.overflowX,
        '--pc-box-overflow-y': this.overflowY,
        ...getResponsiveProps(
          'box',
          'padding-block-start',
          'space',
          this.paddingBlockStart || this.paddingBlock || this.padding
        ),
        ...getResponsiveProps(
          'box',
          'padding-block-end',
          'space',
          this.paddingBlockEnd || this.paddingBlock || this.padding
        ),
        ...getResponsiveProps(
          'box',
          'padding-inline-start',
          'space',
          this.paddingInlineStart || this.paddingInline || this.padding
        ),
        ...getResponsiveProps(
          'box',
          'padding-inline-end',
          'space',
          this.paddingInlineEnd || this.paddingInline || this.padding
        ),
        '--pc-box-shadow': this.shadow ? `var(--p-shadow-${this.shadow})` : undefined,
        '--pc-box-width': this.width,
        position: this.position,
        '--pc-box-inset-block-start': this.insetBlockStart
          ? `var(--p-space-${this.insetBlockStart})`
          : undefined,
        '--pc-box-inset-block-end': this.insetBlockEnd
          ? `var(--p-space-${this.insetBlockEnd})`
          : undefined,
        '--pc-box-inset-inline-start': this.insetInlineStart
          ? `var(--p-space-${this.insetInlineStart})`
          : undefined,
        '--pc-box-inset-inline-end': this.insetInlineEnd
          ? `var(--p-space-${this.insetInlineEnd})`
          : undefined,
        zIndex: this.zIndex,
        opacity: this.opacity,
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/polaris/shared/_responsive-props.scss';

.listReset {
  list-style-type: none;
  margin-block-start: 0;
  margin-block-end: 0;
  outline: none;
  padding-inline-start: 0;
}

.Box {
  @include responsive-props('box', 'padding-block-end', 'padding-block-end');
  @include responsive-props('box', 'padding-block-start', 'padding-block-start');
  @include responsive-props('box', 'padding-inline-start', 'padding-inline-start');
  @include responsive-props('box', 'padding-inline-end', 'padding-inline-end');

  // stylelint-disable -- Polaris component custom properties
  --pc-box-shadow: initial;
  --pc-box-background: initial;
  --pc-box-border-radius: initial;
  --pc-box-border-end-start-radius: var(--pc-box-border-radius);
  --pc-box-border-end-end-radius: var(--pc-box-border-radius);
  --pc-box-border-start-start-radius: var(--pc-box-border-radius);
  --pc-box-border-start-end-radius: var(--pc-box-border-radius);
  --pc-box-color: initial;
  --pc-box-min-height: initial;
  --pc-box-min-width: initial;
  --pc-box-max-width: initial;
  --pc-box-outline-color: initial;
  --pc-box-outline-style: initial;
  --pc-box-outline-width: initial;
  --pc-box-overflow-x: initial;
  --pc-box-overflow-y: initial;
  --pc-box-width: initial;
  --pc-box-border-style: initial;
  --pc-box-border-color: initial;
  --pc-box-border-width: 0;
  --pc-box-border-block-start-width: var(--pc-box-border-width);
  --pc-box-border-block-end-width: var(--pc-box-border-width);
  --pc-box-border-inline-start-width: var(--pc-box-border-width);
  --pc-box-border-inline-end-width: var(--pc-box-border-width);
  --pc-box-inset-block-start: initial;
  --pc-box-inset-block-end: initial;
  --pc-box-inset-inline-start: initial;
  --pc-box-inset-inline-end: initial;
  // stylelint-enable
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  inset-block-start: var(--pc-box-inset-block-start);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  inset-block-end: var(--pc-box-inset-block-end);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  inset-inline-start: var(--pc-box-inset-inline-start);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  inset-inline-end: var(--pc-box-inset-inline-end);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  background-color: var(--pc-box-background);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  box-shadow: var(--pc-box-shadow);
  // stylelint-disable -- generated by polaris-migrator DO NOT COPY
  border-end-start-radius: var(--pc-box-border-end-start-radius);
  // stylelint-enable
  // stylelint-disable -- generated by polaris-migrator DO NOT COPY
  border-end-end-radius: var(--pc-box-border-end-end-radius);
  // stylelint-enable
  // stylelint-disable -- generated by polaris-migrator DO NOT COPY
  border-start-start-radius: var(--pc-box-border-start-start-radius);
  // stylelint-enable
  // stylelint-disable -- generated by polaris-migrator DO NOT COPY
  border-start-end-radius: var(--pc-box-border-start-end-radius);
  // stylelint-enable
  // stylelint-disable-next-line -- component custom property that maps to Polaris tokens
  border-color: var(--pc-box-border-color);
  // stylelint-disable-next-line -- component custom property that maps to Polaris tokens
  border-style: var(--pc-box-border-style);
  // stylelint-disable -- generated by polaris-migrator DO NOT COPY
  border-block-start-width: var(--pc-box-border-block-start-width);
  // stylelint-enable
  // stylelint-disable -- generated by polaris-migrator DO NOT COPY
  border-block-end-width: var(--pc-box-border-block-end-width);
  // stylelint-enable
  // stylelint-disable -- generated by polaris-migrator DO NOT COPY
  border-inline-start-width: var(--pc-box-border-inline-start-width);
  // stylelint-enable
  // stylelint-disable -- generated by polaris-migrator DO NOT COPY
  border-inline-end-width: var(--pc-box-border-inline-end-width);
  // stylelint-enable
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  color: var(--pc-box-color);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  min-height: var(--pc-box-min-height);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  min-width: var(--pc-box-min-width);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  max-width: var(--pc-box-max-width);
  // stylelint-disable-next-line -- component custom property that maps to Polaris tokens
  outline-color: var(--pc-box-outline-color);
  // stylelint-disable-next-line -- component custom property that maps to Polaris tokens
  outline-style: var(--pc-box-outline-style);
  // stylelint-disable-next-line -- component custom property that maps to Polaris tokens
  outline-width: var(--pc-box-outline-width);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  overflow-x: var(--pc-box-overflow-x);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  overflow-y: var(--pc-box-overflow-y);
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  width: var(--pc-box-width);
  -webkit-overflow-scrolling: touch;
}

.visuallyHidden {
  // stylelint-disable-next-line -- generated by polaris-migrator DO NOT COPY
  @include visually-hidden;
}

.printHidden {
  @media print {
    // stylelint-disable-next-line -- necessary display property with !important
    display: none !important;
  }
}
</style>
